import React, { useCallback, useState } from 'react';
import { DashboardCard, ProgressBar, Table } from '@ui/molecules';
import { translate } from '@utils/i18n';
import useTechPerformance from '../hooks/useTechPerformance';
import TechDetailBrushaway from './TechDetailBrushaway';
const namespace = 'features.dashboards.inspection_assistant_insights_v2';
// ── Shared ─────────────────────────────────────────────────────────────────
function TechNameCell({ item }) {
    return (React.createElement("div", { className: "tech-name-cell" },
        React.createElement("div", { className: "tech-name-cell__avatar", style: { backgroundColor: item.avatarColor } }, item.avatarInitials),
        React.createElement("span", { className: "tech-name-cell__name" }, item.name)));
}
// ── Legacy (flag off) helpers ──────────────────────────────────────────────
function getCoachingPriorityConfig(priority) {
    switch (priority) {
        case 'performing_well':
            return { color: '#019064', labelKey: 'tech_perf.performing_well' };
        case 'monitor_coach':
            return { color: '#FFCE0E', labelKey: 'tech_perf.monitor_coach' };
        case 'immediate_intervention':
            return { color: '#DB2B39', labelKey: 'tech_perf.immediate_intervention' };
        default:
            return { color: '#019064', labelKey: 'tech_perf.performing_well' };
    }
}
function getResolutionRateColor(rate) {
    if (rate >= 75)
        return '#019064';
    if (rate >= 40)
        return '#FFCE0E';
    return '#DB2B39';
}
function ResolutionRateCell({ item }) {
    var _a, _b;
    const color = getResolutionRateColor((_a = item.resolutionRate) !== null && _a !== void 0 ? _a : 0);
    return React.createElement(ProgressBar, { value: (_b = item.resolutionRate) !== null && _b !== void 0 ? _b : 0, color: color });
}
function CoachingPriorityCell({ item }) {
    const config = getCoachingPriorityConfig(item.coachingPriority);
    return (React.createElement("div", { className: "coaching-priority-cell" },
        React.createElement("span", { className: "coaching-priority-cell__dot", style: { backgroundColor: config.color } }),
        React.createElement("span", { className: "coaching-priority-cell__label" }, translate(config.labelKey, { namespace }))));
}
const LEGACY_COLUMNS = [
    {
        dataKey: 'name',
        label: translate('tech_perf.col_technician', { namespace }),
        width: '22%',
        columnType: 'primary',
        renderer: TechNameCell
    },
    { dataKey: 'issuesFlagged', label: translate('tech_perf.col_issues_flagged', { namespace }), width: '14%' },
    {
        dataKey: 'resolutionRate',
        label: translate('tech_perf.col_resolution_rate', { namespace }),
        width: '22%',
        renderer: ResolutionRateCell
    },
    { dataKey: 'topIssue', label: translate('tech_perf.col_top_issue', { namespace }), width: '18%' },
    { dataKey: 'avgResubmissions', label: translate('tech_perf.col_avg_resubmissions', { namespace }), width: '14%' },
    {
        dataKey: 'coachingPriority',
        label: translate('tech_perf.col_coaching_priority', { namespace }),
        width: '10%',
        renderer: CoachingPriorityCell
    }
];
// ── IPA scorecard (flag on) helpers ───────────────────────────────────────
function getRatingConfig(rating, status) {
    if (status === 'failed') {
        return { color: '#DB2B39', labelKey: 'tech_perf.ratings.failed', defaultLabel: 'Failed' };
    }
    if (status === 'pending' || status === 'processing' || rating === null) {
        return { color: '#8B90A0', labelKey: 'tech_perf.ratings.pending', defaultLabel: 'Pending' };
    }
    switch (rating) {
        case 'excellent':
            return { color: '#019064', labelKey: 'tech_perf.ratings.excellent', defaultLabel: 'Excellent' };
        case 'good':
            return { color: '#019064', labelKey: 'tech_perf.ratings.good', defaultLabel: 'Good' };
        case 'needs_review':
            return { color: '#9D6B00', labelKey: 'tech_perf.ratings.needs_review', defaultLabel: 'Needs Review' };
        case 'needs_coaching':
            return { color: '#DB2B39', labelKey: 'tech_perf.ratings.needs_coaching', defaultLabel: 'Needs Coaching' };
        default:
            return { color: '#8B90A0', labelKey: 'tech_perf.ratings.pending', defaultLabel: 'Pending' };
    }
}
function RatingCell({ item }) {
    const config = getRatingConfig(item.currentRating, item.status);
    return (React.createElement("div", { className: "coaching-priority-cell" },
        React.createElement("span", { className: "coaching-priority-cell__dot", style: { backgroundColor: config.color } }),
        React.createElement("span", { className: "coaching-priority-cell__label" }, translate(config.labelKey, { namespace, defaultValue: config.defaultLabel }))));
}
function ScoreCell({ item }) {
    if (item.currentScore === null)
        return React.createElement("span", { className: "tech-performance-tab__muted" }, "--");
    return React.createElement("span", { className: "tech-performance-tab__score" },
        item.currentScore,
        "/100");
}
function TrendCell({ item }) {
    var _a;
    const trend = (_a = item.scoreTrend) !== null && _a !== void 0 ? _a : 'neutral';
    return React.createElement("span", { className: `tech-performance-tab__trend tech-performance-tab__trend--${trend}` }, trend);
}
function GeneratedAtCell({ item }) {
    var _a;
    return React.createElement("span", { className: "tech-performance-tab__muted" }, (_a = item.generatedAt) !== null && _a !== void 0 ? _a : '--');
}
const SCORECARD_COLUMNS = [
    {
        dataKey: 'name',
        label: translate('tech_perf.col_technician', { namespace }),
        width: '22%',
        columnType: 'primary',
        renderer: TechNameCell
    },
    {
        dataKey: 'currentRating',
        label: translate('tech_perf.col_rating', { namespace, defaultValue: 'Rating' }),
        width: '18%',
        renderer: RatingCell
    },
    {
        dataKey: 'currentScore',
        label: translate('tech_perf.col_score', { namespace, defaultValue: 'Score' }),
        width: '14%',
        renderer: ScoreCell
    },
    {
        dataKey: 'scoreTrend',
        label: translate('tech_perf.col_trend', { namespace, defaultValue: 'Trend' }),
        width: '14%',
        renderer: TrendCell
    },
    {
        dataKey: 'summary',
        label: translate('tech_perf.col_summary', { namespace, defaultValue: 'Summary' }),
        width: '18%'
    },
    {
        dataKey: 'generatedAt',
        label: translate('tech_perf.col_generated', { namespace, defaultValue: 'Generated' }),
        width: '14%',
        renderer: GeneratedAtCell
    }
];
function TechPerformanceTab({ filters, ipaScorecard = false }) {
    var _a;
    const { data: response, isLoading, isError } = useTechPerformance(filters);
    const data = (_a = response === null || response === void 0 ? void 0 : response.data) !== null && _a !== void 0 ? _a : [];
    const [selectedTech, setSelectedTech] = useState(null);
    const handleRowClick = useCallback(({ item }) => {
        setSelectedTech(item);
    }, []);
    const handleBrushawayClose = useCallback(() => setSelectedTech(null), []);
    const columns = ipaScorecard ? SCORECARD_COLUMNS : LEGACY_COLUMNS;
    let tableContent;
    if (isError) {
        tableContent = (React.createElement("p", { className: "tech-performance-tab__empty" }, translate('tech_perf.error', { namespace, defaultValue: 'Unable to load technician data.' })));
    }
    else if (data.length === 0) {
        // Scorecard mode is not date-scoped — an empty result means no scorecards
        // have been generated yet, not that no tech used IA "in this period".
        const emptyKey = ipaScorecard ? 'tech_perf.empty_scorecard' : 'tech_perf.empty';
        tableContent = React.createElement("p", { className: "tech-performance-tab__empty" }, translate(emptyKey, { namespace }));
    }
    else {
        const hasProcessingRows = ipaScorecard && data.some((row) => row.status === 'pending' || row.status === 'processing');
        tableContent = (React.createElement(React.Fragment, null,
            hasProcessingRows && (React.createElement("p", { className: "tech-performance-tab__status" }, translate('tech_perf.processing', {
                namespace,
                defaultValue: 'Some technician scorecards are still processing.'
            }))),
            React.createElement(Table, { data: data, columns: columns, variant: "detail", size: "full", hover: true, onRowClick: handleRowClick, getItemId: (item) => item.id })));
    }
    return (React.createElement("div", { className: "tech-performance-tab" },
        React.createElement(DashboardCard, { title: translate('tech_perf.title', { namespace }), subtitle: translate('tech_perf.subtitle', { namespace }), isLoading: isLoading },
            tableContent,
            React.createElement(TechDetailBrushaway, { tech: selectedTech, isOpen: !!selectedTech, onClose: handleBrushawayClose }))));
}
export default TechPerformanceTab;
